import * as Yup from 'yup';
import { Action945Enum, BlankEnum, CryoMediaEnum, CryoPurposeEnum, CultureDishConfigEnum, EmbryoFinilizeUpdateType,  NullEnum, StatusDa2Enum } from './embryoCulture.helpers';
import { getRequest, putRequest } from '../../../services/httpService';

// Define the initial embryo culture and update details types
export interface EmbryoCultureType {
    time: string; 
    embryologist?: number | null; 
    witness?: number | null; 
    incubator?: string | null; 
    remarks?: string | null; 
    culture_dish_config?: string | BlankEnum | NullEnum | null; 
    day: number; 
    cycle: number;
    id?: number | null | undefined;
  }
  
export interface OocyteFile {
  embryo_no: number;
  file: string; // URL of the file
}

export interface EmbryoUpdateDetailsType {    
    cycle: number;
    embryos: number[] | null;
    grade: string | null;
    fragmentation_percentage: string | null;
    number_of_cells: string | null;
    action: string | BlankEnum;
    cryo_preserved_date: string | null;
    cryo_preserved_by: number | null;
    cryo_purpose: string | null;
    cryo_media: string | null;
    is_screened: boolean;
    day: number | null;
    status: string | null;
    source?: number | null;
    thaw_cycle?: number | null;
    locations: number | null;
    files?: OocyteFile[];
  }
  
// Define initial values for single object
export const initialEmbryoCultureValues: EmbryoCultureType = {
  embryologist: null,
  witness: null,
  cycle: 0,
  remarks: '',
  culture_dish_config: '',
  day: 2,
  time: '',
  incubator: '',
};
  
export const initialEmbryoUpdateValues: EmbryoUpdateDetailsType = {
  cycle: 0,
  embryos: [],
  action: '',
  grade: '',
  fragmentation_percentage: null,
  number_of_cells: null,
  day: 2,
  status: 'DRAFT',  
  cryo_preserved_date: null,
  cryo_preserved_by: null,
  cryo_purpose: null,
  cryo_media: null,
  is_screened: false,
  locations:  null
};
  
  
// Validation Schema for single embryo culture
export const EmbryoCultureValidationSchema = (tGeneral: (key: string, options?: Record<string, unknown>) => string) => Yup.object().shape({

  embryologist: Yup.number()
    .required(tGeneral('embryologistNameIsRequired')),
  witness: Yup.number()
    .required(tGeneral('witnessEmbryologistNameIsRequired'))
    .test('not-same', tGeneral('witnessEmbryologistCannotBeSame'), function (value) {
      const { embryologist } = this.parent; // Accessing the sibling field
      return value !== embryologist; // Validate that they are not the same
    }),
  
  cycle: Yup.number().integer(),
  remarks: Yup.string().nullable(),
  culture_dish_config: Yup.mixed<CultureDishConfigEnum | BlankEnum | NullEnum>()
    .oneOf([
      'NORMAL_SINGLE_CULTURE', 'EMBRYO_SLIDE_TIMELAPSE_SINGLE_CULTURE', 
      'CULTURE_PRO_TIMELAPSE_SINGLE_CULTURE', 'EMBRYO_SLIDE_IC8_DISH_TIMELAPSE_SINGLE_CULTURE', 
      '38_SPECIAL_GPS_SINGLE_CULTURE', 'EMBRYO_GPS_SINGLE_CULTURE', 
      'MINI_GPS_GPS_SINGLE_CULTURE', '4_WELL_GPS_SINGLE_CULTURE', 
      'U_DROP_GPS_SINGLE_CULTURE', 'EMBRYO_CORRAL_GPS_SINGLE_CULTURE', 
      'NORMAL_GROUP_CULTURE', 'MICROWELL_GROUP_CULTURE', '', 'NULL'
    ])
    .nullable(),
  day: Yup.number().min(0).max(30).integer(),
  time: Yup.string().required(),
  incubator: Yup.string()
    .nullable(),
});
  
// Validation Schema for single embryo update details
export const EmbryoValidationSchema = (tGeneral: (key: string, options?: Record<string, unknown>) => string) => Yup.object().shape({
  cycle: Yup.number().positive(tGeneral('cycleMustBeAPositiveNumber')),
  embryos: Yup.array()
    .min(1, tGeneral('embryoNumberIsRequired'))
    .required(tGeneral('embryoNumberIsRequired')),
  action: Yup.mixed<Action945Enum | BlankEnum>()
    .oneOf(['CULTURE', 'FREEZE', 'DISCARD', ''], 'Invalid action')
    .required(),
  grade: Yup.string().required(),
  fragmentation_percentage: Yup.string().required(),
  number_of_cells: Yup.string().required(),
  day: Yup.number().positive('Day must be a positive number'),
  status: Yup.mixed<StatusDa2Enum>()
    .oneOf(['DRAFT', 'FINALIZED'], 'Invalid status'),
  cryo_preserved_date: Yup.string().nullable(),
  cryo_preserved_by: Yup.number().nullable(),
  cryo_purpose: Yup.mixed<CryoPurposeEnum>()
    .nullable()
    .oneOf(
      [
        'PRESERVATION_OF_EXCESS_EMBRYOS', 
        'TIMED_EMBRYO_TRANSFER', 
        'FERTILITY_PRESERVATION', 
        'GENETIC_TESTING', 
        'FLEXIBLE_FAMILY_PLANNING', 
        'REDUCING_MULTIPLE_PREGNANCIES',
      ], 
      'Invalid cryo purpose'
    ),
  cryo_media: Yup.mixed<CryoMediaEnum>()
    .nullable()
    .oneOf(['KITAZATO', 'SWEMED', 'IRVINE_SCIENTIFIC'], 'Invalid cryo media'),
  is_screened: Yup.boolean(),
});
  
// Combined Validation Schema for single objects
export const CombinedValidationSchema = (tGeneral: (key: string, options?: Record<string, unknown>) => string) => Yup.object().shape({
  embryoCulture: EmbryoCultureValidationSchema(tGeneral),
  embryoDetails: EmbryoValidationSchema(tGeneral),
});



// Function to update embryo details
export function updateEmbryoSummary(id: number, data: EmbryoCultureType): Promise<EmbryoCultureType> {
  const url = `/embryo/cycle/${id}/`;
  return putRequest(url, data, { responseType: 'json' });
}


// Function to get Embryo retrieval
export function getEmbryoDetails(id: number): Promise<EmbryoCultureType> {
  const url =`oocyte-details/${id}`;
  return getRequest(url, { responseType: 'json' });
}

// Function to get Embryo Details
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getEmbryoCultureDetails(id: number, source?: number, thawId?:number | null): Promise<any> {
  let url = `cycle/${id}/embryos/?day=2`;

  // Only append the source parameter if it is provided
  if (source !== undefined) {
    url += `&source=${source}`;
  }

  if (thawId !== undefined && thawId !== null) {
    url += `&thaw_cycle=${thawId}`;
  }
  return getRequest(url, { responseType: 'json' });
}


  
// Function to update Embryo details
export function updateEmbryoDetails(id: number, data: EmbryoCultureType): Promise<EmbryoCultureType> {
  const url = `/embryo-culture/${id}/details/`;
  return putRequest(url, data, { responseType: 'json' });
}

// Function to update Embryo Summary details
export function updateEmbryoSummaryDetails(cycle_id : number, data: EmbryoUpdateDetailsType | EmbryoFinilizeUpdateType): Promise<EmbryoUpdateDetailsType | EmbryoFinilizeUpdateType> {
  const url = `/embryo/cycle/${cycle_id}/`;
  return putRequest(url, data, { responseType: 'json' });
}



export const getColumns = (tGeneral: (key: string, options?: Record<string, unknown>) => string) => [
  { label: tGeneral('embryoNo', { defaultValue: 'Embryo No.' }), field: 'embryoNo', editable: false },
  { label: tGeneral('image', { defaultValue: 'Image' }), field: 'image', editable: false },
  { label: tGeneral('grade', { defaultValue: 'Grade' }), field: 'grade', editable: false },
  { label: tGeneral('fragmentationPercentage', { defaultValue: '% Fragmentation' }), field: 'fragmentation_percentage', editable: false },
  { label: tGeneral('numberOfCells', { defaultValue: '# Cells' }), field: 'number_of_cells', editable: false },
  { label: tGeneral('action', { defaultValue: 'Action' }), field: 'action', editable: false },
  { label: tGeneral('freezeLocation', { defaultValue: 'Freeze Location' }), field: 'location_details', editable: false },
];

export const rows = [];